import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as d3 from 'd3';
import * as func from './ava-sankey.functions';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { drag } from 'd3-drag';
import { pointer } from 'd3-selection';

@Component({
  selector: 'ava-sankey',
  templateUrl: 'ava-sankey.template.html',
  styleUrls: ['ava-sankey.template.css'],
  standalone: false
})
export class AvaSankeyComponent implements OnInit {
  @Input() public height: number;
  @Input() public width: number;
  @Input() public data: any;

  @ViewChild('desc') description;

  NoteVisible = false;
  clientX = '0px';
  clientY = '0px';
  Amount = 0;
  YearAccrued = 0;
  YearPayed = 0;

  constructor(
    private el: ElementRef,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    const graph = this.data;
    const units = '';

    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const width = this.width - margin.left - margin.right;
    const height = this.height - margin.top - margin.bottom;

    const formatNumber = d3.format(',.0f');
    const format = (d: any) => `${formatNumber(d)} ${units}`;
    const color = scaleOrdinal(schemeCategory10);

    const rootEl = d3.select('#sankey');
    const svg = rootEl
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // initialize sankey
    const sankey = func
      .d3sankey()
      .nodeWidth(12)
      .nodePadding(12)
      .size([width, height]);

    const path = sankey.link();
    const defs = svg.append('defs');

    sankey.nodes(graph.nodes).links(graph.links).layout(15);

    const grads = defs.selectAll('linearGradient').data(graph.links, getLinkID);

    grads
      .enter()
      .append('linearGradient')
      .attr('id', getLinkID)
      .attr('gradientUnits', 'objectBoundingBox');

    grads
      .html('')
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', (d: any) =>
        nodeColor(+d.source.x <= +d.target.x ? d.source : d.target)
      );

    grads
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', (d: any) =>
        nodeColor(+d.source.x > +d.target.x ? d.source : d.target)
      );

    const global = this;

    const tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('background', '#fff')
      .style('padding', '6px 10px')
      .style('border', '1px solid #ccc')
      .style('border-radius', '4px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    const link = svg
      .append('g')
      .selectAll('.link')
      .data(graph.links)
      .enter()
      .append('path')
      .attr('class', 'link')
      .attr('d', path)
      .style('stroke-width', (d: any) => Math.max(1, d.dy))
      .style('fill', 'none')
      .style('stroke-opacity', 0.18)
      .sort((a: any, b: any) => b.dy - a.dy)
      .on('mousemove', function (event: MouseEvent, d: any) {
        const [x, y] = pointer(event, this);
        tooltip
          .style('left', x + 20 + 'px')
          .style('top', y + 'px')
          .style('opacity', 1)
          .text(`${d.source.name} → ${d.target.name}: ${format(d.value)}`);
      })
      .on('mouseover', function () {
        d3.select(this).style('stroke-opacity', 0.5);
        global.NoteVisible = true;
      })
      .on('mouseout', function () {
        d3.select(this).style('stroke-opacity', 0.18);
        tooltip.style('opacity', 0);
        global.NoteVisible = false;
      });

    const node = svg
      .append('g')
      .selectAll('.node')
      .data(graph.nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d: any) => `translate(${d.x},${d.y})`)
      .call(
        drag<any, any>()
          .subject((d: any) => d)
          .on('start', function (event, d) {
            this.parentNode.appendChild(this);
          })
          .on('drag', function (event, d) {
            dragmove.call(this, event, d);
          })
      );

    node
      .append('rect')
      .attr('height', (d: any) => d.dy)
      .attr('width', sankey.nodeWidth())
      .style('fill', (d: any) => (d.color = color(d.name)))
      .append('title')
      .text((d: any) => `${d.name}\n${format(d.value)}`);

    node
      .append('text')
      .attr('x', -6)
      .attr('y', (d: any) => d.dy / 2)
      .attr('dy', '.35em')
      .attr('text-anchor', 'end')
      .attr('transform', null)
      .text((d: any) => d.name)
      .filter((d: any) => d.x < width / 2)
      .attr('x', 6 + sankey.nodeWidth())
      .attr('text-anchor', 'start');

    link.style('stroke', (d: any, i: any) => {
      const gradientID = `gradient${i}`;
      const startColor = d.source.color;
      const stopColor = d.target.color;

      const linearGradient = defs
        .append('linearGradient')
        .attr('id', gradientID);

      linearGradient
        .selectAll('stop')
        .data([
          { offset: '10%', color: startColor },
          { offset: '90%', color: stopColor }
        ])
        .enter()
        .append('stop')
        .attr('offset', (d: any) => d.offset)
        .attr('stop-color', (d: any) => d.color);

      return `url(#${gradientID})`;
    });

    function dragmove(event: any, d: any) {
      d3.select(this).attr(
        'transform',
        `translate(${(d.x = Math.max(
          0,
          Math.min(width - d.dx, event.x)
        ))},${(d.y = Math.max(0, Math.min(height - d.dy, event.y)))})`
      );
      sankey.relayout();
      link.attr('d', path);
    }

    function getLinkID(d: any) {
      return 'link-' + d.source.name + '-' + d.target.name;
    }

    function nodeColor(d: any) {
      return (d.color = color(d.name.replace(/ .*/, '')));
    }
  }

  setCoordinates(el: any, p: [number, number]) {
    const [x, y] = p;
    this.clientX = x - 28 + 'px';
    this.clientY = y - 59 + 'px';
    this.Amount = this.formatNumber(el.value, 0);
    this.YearAccrued = el.source.name;
    this.YearPayed = el.target.name;
  }

  formatNumber(x: any, decimal: number = 2) {
    if ((!x && x !== 0) || String(x).includes('NaN')) return '';
    return x.toLocaleString(this.translateService.currentLang, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal
    });
  }
}
